import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { EFFICIENCY_LEVEL } from '../types';
import { getEfficiencyLevel } from './PerformanceCellBadge';
import { PerformanceTimeline } from './PerformanceTimeline';
import type { ProjectPerformanceEntry } from '../types';

const EFFICIENCY_BADGE: Record<(typeof EFFICIENCY_LEVEL)[keyof typeof EFFICIENCY_LEVEL], string> = {
  [EFFICIENCY_LEVEL.OPTIMAL]: 'bg-green-100 text-green-700',
  [EFFICIENCY_LEVEL.GOOD]: 'bg-blue-100 text-blue-700',
  [EFFICIENCY_LEVEL.REGULAR]: 'bg-amber-100 text-amber-700',
  [EFFICIENCY_LEVEL.LOW]: 'bg-red-100 text-red-700',
};

interface ProjectAccordionItemProps {
  project: ProjectPerformanceEntry;
  periodStart: string;
  periodEnd: string;
}

export function ProjectAccordionItem({
  project,
  periodStart,
  periodEnd,
}: ProjectAccordionItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const efficiencyLevel = getEfficiencyLevel(project.efficiency);

  return (
    <div
      data-testid={`labor-performance-project-item-${project.projectId}`}
      className="rounded-lg border"
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 rounded-lg"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-3 w-3 flex-shrink-0 rounded-full bg-primary" />
          <span className="truncate text-sm font-semibold">{project.projectName}</span>
          <span className="flex-shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {project.memberCount} colaboradores
          </span>
          <span
            className={cn(
              'flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold',
              EFFICIENCY_BADGE[efficiencyLevel],
            )}
          >
            {project.efficiency}% eficiencia
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        )}
      </button>

      {isOpen && (
        <div className={cn('border-t p-3')}>
          <PerformanceTimeline
            entries={project.members}
            periodStart={periodStart}
            periodEnd={periodEnd}
            showLegend={false}
          />
        </div>
      )}
    </div>
  );
}
